/**
 * CQT platform — theme preference (light / dark / system).
 *
 * The *preference* is one of three values and is what the user picks.
 * The *resolved* theme is always "light" or "dark" and is what lands on
 * <html data-theme>. CSS only ever sees the resolved value, so no stylesheet
 * has to know that "system" exists.
 *
 * First paint is handled by packages/brand/theme-init.js (injected inline).
 * This module takes over afterwards and keeps the attribute in sync.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export type ThemePref = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

/** Must match the key used in theme-init.js. */
export const THEME_STORAGE_KEY = "cqt-theme";

const DARK_QUERY = "(prefers-color-scheme: dark)";

function readStoredPref(): ThemePref {
  try {
    const v = localStorage.getItem(THEME_STORAGE_KEY);
    if (v === "light" || v === "dark" || v === "system") return v;
  } catch {
    /* Safari private mode and friends. Fall through to system. */
  }
  return "system";
}

function systemTheme(): ResolvedTheme {
  return typeof window !== "undefined" && window.matchMedia(DARK_QUERY).matches
    ? "dark"
    : "light";
}

function resolve(pref: ThemePref): ResolvedTheme {
  return pref === "system" ? systemTheme() : pref;
}

interface ThemeContextValue {
  /** What the user chose. Persisted. */
  pref: ThemePref;
  /** What is actually rendered right now. */
  resolved: ResolvedTheme;
  setPref: (pref: ThemePref) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [pref, setPrefState] = useState<ThemePref>(readStoredPref);
  const [resolved, setResolved] = useState<ResolvedTheme>(() => resolve(readStoredPref()));

  // Apply the resolved theme to <html>. theme-init.js already did this for the
  // first paint; this keeps it correct on every later change.
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", resolved);
  }, [resolved]);

  // Follow the OS only while the preference is "system".
  useEffect(() => {
    if (pref !== "system") {
      setResolved(pref);
      return;
    }
    const mq = window.matchMedia(DARK_QUERY);
    const sync = () => setResolved(mq.matches ? "dark" : "light");
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, [pref]);

  // Cross-tab sync: change the theme in one tab, every tab follows.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === THEME_STORAGE_KEY) setPrefState(readStoredPref());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Enable colour transitions one frame after mount, so neither the initial
  // paint nor the pre-paint theme application animates.
  useEffect(() => {
    const id = requestAnimationFrame(() =>
      document.documentElement.classList.add("theme-anim"),
    );
    return () => cancelAnimationFrame(id);
  }, []);

  const setPref = useCallback((next: ThemePref) => {
    setPrefState(next);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      /* Non-fatal: the theme just won't survive a reload. */
    }
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({ pref, resolved, setPref }),
    [pref, resolved, setPref],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used inside <ThemeProvider>");
  return ctx;
}

/* ------------------------------------------------------------------ icons */

function SunIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" aria-hidden="true">
      <circle cx="12" cy="12" r="4.2" />
      <path d="M12 2.6v2.2M12 19.2v2.2M2.6 12h2.2M19.2 12h2.2M5.4 5.4l1.6 1.6M17 17l1.6 1.6M18.6 5.4L17 7M7 17l-1.6 1.6" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20.5 14.6A8.6 8.6 0 1 1 9.4 3.5a6.8 6.8 0 0 0 11.1 11.1z" />
    </svg>
  );
}

function SystemIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2.5" y="4" width="19" height="12.5" rx="1.6" />
      <path d="M8.5 20.5h7M12 16.5v4" />
    </svg>
  );
}

const OPTIONS: { pref: ThemePref; label: string; icon: () => ReactNode }[] = [
  { pref: "light", label: "Light theme", icon: SunIcon },
  { pref: "dark", label: "Dark theme", icon: MoonIcon },
  { pref: "system", label: "Match system theme", icon: SystemIcon },
];

/**
 * Three-way theme switcher.
 *
 * Rendered as a real radiogroup so a screen reader announces it as one control
 * with three states, and arrow keys move between them.
 *
 * @param onPlate set when the control sits on the dark brand header, so it
 *   picks the on-plate colours instead of the current theme's surface colours.
 */
export function ThemeToggle({ onPlate = false, translateLabel = (value: string) => value }: { onPlate?: boolean; translateLabel?: (value: string) => string }) {
  const { pref, setPref } = useTheme();

  const move = (dir: 1 | -1) => {
    const i = OPTIONS.findIndex((o) => o.pref === pref);
    // findIndex returns -1 if the stored preference is somehow not one of the
    // three; the modulo keeps the result in range either way, and the fallback
    // satisfies noUncheckedIndexedAccess without an assertion.
    const next = OPTIONS[(i + dir + OPTIONS.length) % OPTIONS.length] ?? OPTIONS[0];
    if (next) setPref(next.pref);
  };

  return (
    <div
      role="radiogroup"
      aria-label={translateLabel("Colour theme")}
      className={`segmented${onPlate ? " segmented--on-plate" : ""}`}
      onKeyDown={(e) => {
        if (e.key === "ArrowRight" || e.key === "ArrowDown") {
          e.preventDefault();
          move(1);
        } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
          e.preventDefault();
          move(-1);
        }
      }}
    >
      {OPTIONS.map(({ pref: p, label, icon: Icon }) => {
        const checked = pref === p;
        return (
          <button
            key={p}
            type="button"
            role="radio"
            aria-checked={checked}
            aria-label={translateLabel(label)}
            title={translateLabel(label)}
            // Only the selected radio is in the tab order; arrows do the rest.
            tabIndex={checked ? 0 : -1}
            className="segmented__btn"
            onClick={() => setPref(p)}
          >
            <Icon />
          </button>
        );
      })}
    </div>
  );
}
